import { Badge, Banner, Button, Loader, Table, Text } from '@cloudflare/kumo'
import type { AdminConfirmedEventView, ConfirmedEvent } from '@repo/shared'
import { useEffect, useState } from 'react'
import { request } from '../api'
import { useGetToken } from '../lib/auth'
import { fmtTime, toMessage } from '../lib/format'
import { TablePagination } from '../lib/table-pagination'
import { usePagedList } from '../lib/use-page'

// 관리자 웹은 기존 운영 도구와 동일하게 한국어로 표시한다. 고객 문구가 아니다.
const EVENT: Record<ConfirmedEvent, string> = {
  generation_completed: '생성 완료',
  generation_failed: '생성 실패',
  generation_cancelled: '생성 취소',
  refund_verified: '환불 확정',
  subscription_cancelled: '구독 해지 예약',
  subscription_expired: '구독 만료',
}

/** 환불 확정 중 이미 쓴 권리가 있던 건. 10/29 대표 결정: 쓴 견적권이 있으면 환불 요청은 거절을 권한다 */
const USED_BEFORE_REFUND = ['used_before_refund', 'already_used', 'partly_used']

function summary(item: AdminConfirmedEventView): string {
  const d = item.detail ?? {}
  if (item.event.startsWith('generation_')) {
    const credit = d.credit === 'pack' ? '생성 팩' : '플랜 한도'
    if (item.event === 'generation_completed') return `${credit} 사용`
    return `${credit} ${d.creditRestored ? '복구' : '복구 안 함'}${d.code ? ` · ${String(d.code)}` : ''}`
  }
  const parts = [d.productId, d.status, d.grantAction, d.reason].filter(
    (v) => v !== null && v !== undefined && v !== '',
  )
  return parts.map(String).join(' · ') || '—'
}

/** 사용자 한 명의 서버 확정 이벤트 (Top10 #8). 읽기 전용 */
export function ConfirmedEventsPanel({ userId }: { userId: string }) {
  const getToken = useGetToken()
  const [items, setItems] = useState<AdminConfirmedEventView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reload, setReload] = useState(0)
  const page = usePagedList(items, 10)
  useEffect(() => {
    let current = true
    setItems(null)
    setError(null)
    request<{ items: AdminConfirmedEventView[] }>(
      getToken,
      `/admin/users/${encodeURIComponent(userId)}/events`,
    )
      .then((result) => {
        if (current) setItems(result.items)
      })
      .catch((cause: unknown) => {
        if (current) setError(toMessage(cause))
      })
    return () => {
      current = false
    }
  }, [getToken, userId, reload])
  return (
    <section className="flex flex-col gap-3" aria-label="서버 확정 이벤트">
      <div className="flex items-center justify-between gap-3">
        <Text variant="heading">서버 확정 이벤트</Text>
        <Button variant="ghost" size="sm" onClick={() => setReload((value) => value + 1)}>
          새로고침
        </Button>
      </div>
      <Text variant="secondary" size="xs">
        서버가 상태를 바꾼 순간의 기록이에요(최근 50개). 생성 성공·실패·취소와 사용권 복구, 환불
        확정, 구독 해지·만료를 앱 분석과 상관없이 남겨요.
      </Text>
      {error ? (
        <Banner variant="error" title="기록을 불러오지 못했어요" description={error} />
      ) : items === null ? (
        <Loader />
      ) : items.length === 0 ? (
        <Text variant="secondary" size="sm">
          기록된 이벤트가 없어요.
        </Text>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>시각</Table.Head>
                  <Table.Head>이벤트</Table.Head>
                  <Table.Head>내용</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {page.slice.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>{fmtTime(item.createdAt)}</Table.Cell>
                    <Table.Cell>
                      {EVENT[item.event] ?? item.event}
                      {item.detail?.isSandbox === true && ' · 테스트'}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="max-w-80 break-all text-xs">{summary(item)}</div>
                      {item.event === 'refund_verified' &&
                        USED_BEFORE_REFUND.includes(String(item.detail?.grantAction)) && (
                          <Badge variant="warning">쓴 권리 있음 · 환불 요청은 거절 권장</Badge>
                        )}
                      {(item.simulationId ?? item.paymentEventId) && (
                        <div className="text-kumo-subtle max-w-80 break-all font-mono text-xs">
                          {item.simulationId ?? item.paymentEventId}
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          <TablePagination
            page={page.page}
            setPage={page.setPage}
            perPage={page.perPage}
            total={page.total}
          />
        </>
      )}
    </section>
  )
}
